import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface StrategyAnalytics {
  strategy_instance_id: string;
  strategy_name: string;
  status: string;
  user_id: string;
  risk_mode: string;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  win_rate: number;
  net_profit: number;
  total_profit: number;
  total_loss: number;
  profit_factor: number;
  max_drawdown: number;
  sharpe_ratio: number;
  last_updated: string | null;
  active_users: number;
  popularity_score: number;
}

export interface StrategyOfTheDayData extends StrategyAnalytics {
  score: number;
  has_sufficient_data: boolean;
}

/**
 * Hook to fetch strategy analytics from the strategy_analytics_view.
 * Provides real performance metrics for all user's strategy instances.
 */
export function useStrategyAnalytics() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState<StrategyAnalytics[]>([]);
  const [strategyOfTheDay, setStrategyOfTheDay] = useState<StrategyOfTheDayData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setAnalytics([]);
      setLoading(false);
      return;
    }

    fetchAnalytics();
  }, [user]);

  const fetchAnalytics = async () => {
    if (!user) return;

    try {
      // Query strategy_analytics_view (it's a SQL view, query via rpc or direct)
      // Since views appear as tables in PostgREST, we can query directly
      const { data, error } = await supabase
        .from('strategy_analytics_view' as any)
        .select('*')
        .eq('user_id', user.id);

      if (error) {
        console.error('[useStrategyAnalytics] Error fetching:', error.message);
        setAnalytics([]);
      } else {
        const results = (data || []) as unknown as StrategyAnalytics[];
        setAnalytics(results);

        // Calculate Strategy of the Day
        const sotd = computeStrategyOfTheDay(results);
        setStrategyOfTheDay(sotd);
      }
    } catch (err) {
      console.error('[useStrategyAnalytics] Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  };

  return { analytics, strategyOfTheDay, loading, refetch: fetchAnalytics };
}

/**
 * Compute the "Strategy of the Day" using a scoring formula.
 * score = (win_rate * 0.4) + (profit_factor * 0.3) + (popularity_score * 0.3)
 * Only considers strategies with >= 5 trades.
 */
function computeStrategyOfTheDay(analytics: StrategyAnalytics[]): StrategyOfTheDayData | null {
  if (analytics.length === 0) return null;

  let best: StrategyOfTheDayData | null = null;
  let bestScore = -Infinity;

  for (const a of analytics) {
    const hasSufficientData = a.total_trades >= 5;
    const score = (a.win_rate * 0.4) + (a.profit_factor * 0.3) + (a.popularity_score * 0.3);

    const candidate: StrategyOfTheDayData = {
      ...a,
      score,
      has_sufficient_data: hasSufficientData,
    };

    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return best;
}
